// i18n.js
import enUrl from "raw:../locales/en.json";
import esUrl from "raw:../locales/es.json";

const localeUrls = {
  en: enUrl,
  es: esUrl,
};
export class Translator {
  constructor() {
    this.currentTranslations = {};
    this.currentLang = "en";
    this.userData = {};

    // Define languages your application actually supports
    this.supportedLanguages = ["en", "es"];
    this.defaultFallbackLanguage = "en";

    // Toggle this to false in production to keep console clean
    this.debugMode = true;
  }

  setGlobalData(data) {
    this.userData = { ...this.userData, ...data };
  }

  // UPDATED: Core string replacement logic with built-in missing key detection
  t(key, elementData = {}) {
    // 1. Check if the key actually exists in our downloaded dictionary file
    if (!this.currentTranslations.hasOwnProperty(key)) {
      if (this.debugMode) {
        console.warn(
          `[i18n Warning]: Missing translation key "${key}" for language "${this.currentLang}"`,
        );
      }

      // Fallback strategy: return the key text cleanly formatted so the UI layout doesn't break
      // e.g., "missing_key_name" becomes "[missing_key_name]"
      return `[${key}]`;
    }

    let translation = this.currentTranslations[key];
    const combinedData = { ...this.userData, ...elementData };

    Object.keys(combinedData).forEach((placeholder) => {
      translation = translation.replaceAll(
        `{${placeholder}}`,
        combinedData[placeholder],
      );
    });

    return translation;
  }

  // NEW: Recursive deep scanner that bypasses Shadow DOM boundaries
  translatePage() {
    // Define a helper closure to handle the recursive crawl
    const scanNode = (rootNode) => {
      if (!rootNode) return;

      // Target any element that has either translation tag setup configured
      rootNode
        .querySelectorAll("[data-i18n], [data-i18n-attr]")
        .forEach((element) => {
          const textKey = element.getAttribute("data-i18n");
          const attrConfigStr = element.getAttribute("data-i18n-attr");

          // Read variable data parameters (if any)
          const dataAttr = element.getAttribute("data-i18n-data");
          const elementData = dataAttr ? JSON.parse(dataAttr) : {};

          // 1. Handle Standard Inner Text Content Translation
          if (textKey) {
            element.textContent = this.t(textKey, elementData);
          }

          // 2. Handle Multiple Attribute Translations Dynamic Setup
          if (attrConfigStr) {
            try {
              // Try parsing the attribute value as a JSON mapping configuration object
              const attrMapping = JSON.parse(attrConfigStr);

              // Loop over each specified target attribute (e.g., placeholder, title)
              Object.keys(attrMapping).forEach((targetHtmlAttr) => {
                const dictionaryKey = attrMapping[targetHtmlAttr];
                const translatedValue = this.t(dictionaryKey, elementData);

                element.setAttribute(targetHtmlAttr, translatedValue);
              });
            } catch (e) {
              // FALLBACK: If it isn't valid JSON, treat it like our old single string approach
              if (textKey) {
                element.setAttribute(
                  attrConfigStr,
                  this.t(textKey, elementData),
                );
              }
            }
          }
        });

      // Deep crawl past any open component roots
      rootNode.querySelectorAll("*").forEach((childElement) => {
        if (childElement.shadowRoot) {
          scanNode(childElement.shadowRoot);
        }
      });
    };

    // Kick off the initial scan starting from the main document body
    scanNode(document.body);
  }

  async changeLanguage(lang) {
    try {
      // Fallback validation check
      if (!this.supportedLanguages.includes(lang)) {
        lang = this.defaultFallbackLanguage;
      }
      const fileUrl = localeUrls[lang];
      if (!fileUrl) {
        console.error(`Locale ${lang} not supported`);
        return;
      }
      const response = await fetch(fileUrl);

      if (!response.ok) throw new Error(`Could not load translations`);

      this.currentTranslations = await response.json();
      this.currentLang = lang;

      this.translatePage();
      document.documentElement.lang = lang;
      localStorage.setItem("preferredLanguage", lang);

      const event = new CustomEvent("app:lang-changed", {
        detail: { language: lang },
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error("Translation Engine Error:", error);
    }
  }

  // NEW DETECT AND INITIALIZE SYSTEM
  init(dropdownId) {
    // 1. Check if user has an explicit manual save in localStorage
    const savedLang = localStorage.getItem("preferredLanguage");

    // 2. Read browser settings and isolate the first two letters (e.g., "en-US" -> "en")
    const browserLang = navigator.language.slice(0, 2);

    // 3. Determine target language: Saved Preference -> Browser Language -> Default Fallback
    let targetedLanguage = this.defaultFallbackLanguage;

    if (savedLang && this.supportedLanguages.includes(savedLang)) {
      targetedLanguage = savedLang;
    } else if (this.supportedLanguages.includes(browserLang)) {
      targetedLanguage = browserLang;
    }

    // 4. Update the visual dropdown selection state matching our decision
    const switcher = document.getElementById(dropdownId);
    if (switcher) {
      switcher.value = targetedLanguage;
      switcher.addEventListener("change", (e) =>
        this.changeLanguage(e.target.value),
      );
    }

    // 5. Fire up the translation dictionary pipeline
    this.changeLanguage(targetedLanguage);
  }

  initFromElement(switcherElement) {
    const savedLang = localStorage.getItem("preferredLanguage");
    const browserLang = navigator.language.slice(0, 2);
    let targetedLanguage = this.defaultFallbackLanguage;
    console.log("lang chosen: ", targetedLanguage);

    if (savedLang && this.supportedLanguages.includes(savedLang)) {
      targetedLanguage = savedLang;
    } else if (this.supportedLanguages.includes(browserLang)) {
      targetedLanguage = browserLang;
    }

    if (switcherElement) {
      switcherElement.value = targetedLanguage;
      switcherElement.addEventListener("change", (e) =>
        this.changeLanguage(e.target.value),
      );
    }

    this.changeLanguage(targetedLanguage);
  }
}

// Export a single shared instance of the translator
export const i18n = new Translator();
